import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, SegmentedControl } from '@/components/ui';
import {
  setUpHrPeople,
  type HrPersonRef,
  type HrSetUpResult,
} from '@/api/services/hrWorkspace.service';
import { getErrorMessage } from '@/lib/getErrorMessage';

/** Matches the server's batch limit. */
const BATCH_SIZE = 50;

export type SetUpTarget = HrPersonRef & { name: string };

type Outcome = { succeeded: number; failed: Array<{ name: string; reason: string }> };

/**
 * Starts employment records from what each person's profile already says, so
 * HR can clear a whole list in one go instead of filling a form per person.
 */
export function SetUpPeopleDialog({
  targets,
  onEnterDetails,
  onClose,
}: {
  targets: SetUpTarget[];
  /** Offered for one person, for HR who want to choose managed job details now. */
  onEnterDetails?: () => void;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'active' | 'probation'>('active');
  const [done, setDone] = useState(0);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const count = targets.length;
  const noun = count === 1 ? 'person' : 'people';

  const run = async (): Promise<void> => {
    setRunning(true);
    setError(null);
    const results: HrSetUpResult[] = [];
    try {
      for (let start = 0; start < count; start += BATCH_SIZE) {
        const batch = targets
          .slice(start, start + BATCH_SIZE)
          .map(({ companyId, userId }) => ({ companyId, userId }));
        results.push(...(await setUpHrPeople(batch, status)));
        setDone(results.length);
      }
    } catch (runError) {
      // People already done stay done; a retry reports them as already set up.
      setError(getErrorMessage(runError));
    }
    const nameOf = (result: HrSetUpResult): string =>
      targets.find(
        (target) => target.userId === result.userId && target.companyId === result.companyId
      )?.name ?? 'Unknown person';
    setOutcome({
      succeeded: results.filter((result) => result.status === 'set_up').length,
      failed: results
        .filter((result) => result.status === 'failed')
        .map((result) => ({ name: nameOf(result), reason: result.reason ?? 'Could not set up' })),
    });
    setRunning(false);
    for (const queryKey of [['hr-employments'], ['hr-overview'], ['hr-movements-directory']]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };

  if (outcome) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Setup finished"
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="space-y-3" role="status">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {outcome.succeeded} of {count} {noun} set up.
            {outcome.succeeded > 0 &&
              ' They now appear in the org chart, job changes, probation and letters.'}
          </p>
          {error && (
            <p className="text-sm text-danger" role="alert">
              Setup stopped early: {error}
            </p>
          )}
          {outcome.failed.length > 0 && (
            <ul className="max-h-64 divide-y divide-gray-200 overflow-y-auto rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
              {outcome.failed.map((item, index) => (
                <li key={`${item.name}-${index}`} className="px-4 py-2.5">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {item.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">{item.reason}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={() => !running && onClose()}
      title={count === 1 ? `Set up ${targets[0].name}` : `Set up ${count} people`}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" disabled={running} onClick={onClose}>
            Cancel
          </Button>
          <Button loading={running} onClick={() => void run()}>
            {running ? `Setting up ${done} of ${count}` : `Set up ${count === 1 ? '' : count}`.trim()}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-gray-700 dark:text-gray-200">
        <p>
          Creates an employment record so they appear in the org chart, job changes, probation and
          letters.
        </p>
        <ul className="list-disc space-y-1 pl-5 text-gray-600 dark:text-gray-300">
          <li>Joining date, department, job title and manager come from the profile.</li>
          <li>Someone in more than one company starts with blank job details.</li>
          <li>Afterwards, job details change through Job changes.</li>
        </ul>
        <div>
          <p className="mb-2 font-medium text-gray-900 dark:text-gray-100">Start them as</p>
          <SegmentedControl
            ariaLabel="Starting status"
            value={status}
            onChange={setStatus}
            disabled={running}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'probation', label: 'On probation' },
            ]}
          />
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
            Anyone whose profile already says probation stays on probation.
          </p>
        </div>
        {onEnterDetails && (
          <button
            type="button"
            disabled={running}
            onClick={onEnterDetails}
            className="text-sm font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50 dark:text-sky-400"
          >
            Enter job details by hand instead
          </button>
        )}
      </div>
    </Modal>
  );
}
